import React, { useRef } from 'react'

import { Box } from '@gousto-internal/citrus-react'
import { DeviceType, useDeviceType } from '@library/dom'

import { Carousel, type CarouselProps } from '../../EndlessVarietySection/Carousel'
import { useIsDesktopBreakpoint } from '../../../hooks/useIsDesktopBreakpoint'
import { LandingPageContentSections } from '../../../tracking'
import { useIsIntersecting } from '../../useIsIntersecting'
import { RECIPE_VIDEOS } from '../config'
import { CWCRecipeVideoCard } from './CWCRecipeVideoCard'

const propertiesForTracking = {
  carouselSection: LandingPageContentSections.HOMEPAGE_REVIEWS,
}

export const CWCRecipeVideosCarousel = () => {
  const deviceType = useDeviceType()
  const isDesktopDevice = useIsDesktopBreakpoint()
  const isMobileDevice = deviceType === DeviceType.MOBILE
  const containerRef = useRef<HTMLDivElement>(null)

  // Drives every recipe video's play/pause together (see CWCVideoCard's `shouldPlay` prop), based on
  // this carousel's own visibility rather than each video's individual visibility.
  const shouldPlay = useIsIntersecting(containerRef, 0.5)

  const carouselOptions: CarouselProps['options'] = isDesktopDevice
    ? { align: 'center' as const, containScroll: 'trimSnaps', loop: false }
    : { align: 'center' as const, loop: false }

  return (
    <Box data-testid="recipe-videos-carousel" ref={containerRef}>
      <Carousel
        carouselAreaLabel="recipeVideosCarousel"
        options={carouselOptions}
        slidePaddingH={2}
        slidePaddingV={1}
        showNavButtons={false}
        startAtMiddleAvailableSnap={isMobileDevice}
        centerSlidesWhenNotScrollable
        snowplowTrackingProperties={propertiesForTracking}
      >
        {RECIPE_VIDEOS.map(({ videoURL, recipeName, rating, tile }) => (
          // The card's 50px-blur drop shadow stays faintly visible ~73px below it; the shared
          // Carousel clips overflow on both axes, so pad the slide's bottom generously to keep the
          // shadow's full gradual fade from being cut. The section re-balances the following gap.
          <Box key={recipeName} paddingBottom={20}>
            <CWCRecipeVideoCard
              videoURL={videoURL}
              recipeName={recipeName}
              rating={rating}
              tile={tile}
              shouldPlay={shouldPlay}
            />
          </Box>
        ))}
      </Carousel>
    </Box>
  )
}
